import React from 'react';

import { css } from '@compiled/react';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import type { IconSize } from '@/libraries/design/foundations/icon';

import type { NavigationTabsAppearance } from './navigation-tabs-section';

const iconDimensions: Record<IconSize, string> = {
	'x-small': '24px',
	small: '32px',
	medium: '40px',
	large: '48px',
};
const linkCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	color: 'inherit',
	textDecoration: 'none',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});
const wideLinkCss = css({
	gap: '8px',
	padding: '12px 16px',
	border: '2px solid transparent',
	borderRadius: '100px',
});
const compactLinkCss = css({
	flexDirection: 'column',
	gap: '4px',
	minWidth: '140px',
	padding: '10px',
	borderRadius: '10px',
	textAlign: 'center',
});
const inactiveLinkCss = css({ backgroundColor: wacToken('color.neutral.light200') });
const wideInactiveLinkCss = css({ backgroundColor: wacToken('color.neutral.light100') });
const compactInactiveLinkCss = css({
	backgroundColor: wacToken('color.neutral.light100'),
});
const activeLinkCss = css({
	backgroundColor: wacToken('color.extended.blue100'),
	fontWeight: 700,
	textDecoration: 'underline',
});
const wideActiveLinkCss = css({ border: `2px solid ${wacToken('color.primary.blue')}` });
const compactIconCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '10px 16px',
});
const noIconPaddingCss = css({ padding: 0 });

export type NavigationTabsLinkProps = {
	title: string;
	href: string;
	iconUrl?: string;
	iconSize?: IconSize;
	disableIconPadding?: boolean;
	appearance: NavigationTabsAppearance;
	isActive: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const NavigationTabsLink = ({
	title,
	href,
	iconUrl,
	iconSize,
	disableIconPadding,
	appearance,
	isActive,
	onClick,
}: NavigationTabsLinkProps) => {
	const size = iconSize ?? 'x-small';
	const icon = iconUrl && (
		<Image
			src={iconUrl}
			alt=""
			width={iconDimensions[size]}
			height={iconDimensions[size]}
			draggable={false}
		/>
	);
	return (
		<a
			css={[
				linkCss,
				appearance === 'wide' ? wideLinkCss : compactLinkCss,
				isActive ? activeLinkCss : inactiveLinkCss,
				appearance === 'wide' && !isActive && wideInactiveLinkCss,
				appearance === 'compact' && !isActive && compactInactiveLinkCss,
				appearance === 'wide' && isActive && wideActiveLinkCss,
			]}
			href={href}
			aria-current={isActive ? 'page' : undefined}
			onClick={onClick}
			data-product-key="uniform"
			data-event="clicked"
			data-event-component="linkButton"
			data-event-container="navigationTabs"
		>
			{appearance === 'compact' && icon ? (
				<span css={[compactIconCss, disableIconPadding && noIconPaddingCss]}>{icon}</span>
			) : (
				icon
			)}
			{title}
		</a>
	);
};
